import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { map, catchError, tap, switchMap, concatMap } from 'rxjs/operators';
import * as AuthActions from './auth.actions';
import { AuthService } from '@/app/pages/auth/services/auth.service';
import { AuthUser, LoginResponse, Function } from '@/app/pages/auth/models/auth.model';
import { HttpClient } from '@angular/common/http';
import { ApiResponse } from '@/app/core/models/api.model';
import { NotificationService } from '@/app/core/services/common/notification.service';
import { CookieService } from '@app/core/services/common/cookie.service';
import {
  UserInfoKey,
  MenuInfoKey,
  TokenInfoKey,
  RefreshTokenExpirationKey,
  FunctionInfoKey,
  AccessTokenExpirationKey,
} from '@/app/shared/constants';
import { Menu } from '@/app/pages/menu-system/models/menu.model';
import { I18nService } from '@/app/core/services/common/i18n.service';
import { EnvService } from '@/app/core/services/common/env.service';

@Injectable()
export class AuthEffects {
  private readonly envService = inject(EnvService);
  private readonly notification = inject(NotificationService);
  private readonly http = inject(HttpClient);
  private readonly cookieService = inject(CookieService);
  private readonly authService = inject(AuthService);
  private readonly i18n = inject(I18nService);
  private actions$ = inject(Actions);
  private router = inject(Router);

  /**
   * Get API URL - computed dynamically từ env config
   * Tính toán mỗi lần gọi để đảm bảo lấy config mới nhất
   */
  private getApiURL(): string {
    return `${this.envService.getApiUrl()}/api/auth`;
  }

  // Initialize auth - call API to refresh user info if refresh token exists
  initializeAuth$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.initializeAuth),
        concatMap(() => {
          const accessToken = this.cookieService.getCookie(TokenInfoKey);
          const userData = this.cookieService.getCookie(UserInfoKey);
          const menuData = sessionStorage.getItem(MenuInfoKey);
          const functionData = sessionStorage.getItem(FunctionInfoKey);

          if (accessToken && userData && !this.authService.hasExpiredAccessToken()) {
            // If refresh token exists and not expired, call API to refresh user info
            const user = userData ? JSON.parse(userData) : null;
            if (user && user.UserId && user.LoginName) {
              const actions = [
                AuthActions.setAuthUserFromStorage({ user }),
                AuthActions.setMenus({ menus: menuData ? JSON.parse(menuData) : [] }),
                AuthActions.loadPermissions({
                  permissions: functionData ? JSON.parse(functionData) : [],
                }),
                AuthActions.refreshUserInfo({
                  userId: user.UserId,
                  loginName: user.LoginName,
                }),
              ];
              // Dispatch refresh user info action
              return of(...actions);
            }
          }

          // If no refresh token or expired, logout
          return of(AuthActions.logoutSuccess());
        }),
      ),
    { dispatch: true },
  );

  // Login
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      switchMap(({ email, password, rememberMe, branchId, branchCode }) => {
        // Lấy URL mỗi lần gọi API để đảm bảo config mới nhất
        const apiURL = this.getApiURL();

        return this.http
          .post<ApiResponse<LoginResponse[]>>(apiURL + '/login', {
            JsonHeader: {
              lang: this.i18n.getCurrentLang(),
              user: email,
              channel: 'BACK',
              branchCode: branchCode || '',
              branchId: branchId || 0,
            },
            LoginName: email,
            Password: password,
            branchId: branchId || 0,
          })
          .pipe(
            //TODO: change use object LoginResponse instead of LoginResponse[] after when backend fixed
            map((response: ApiResponse<LoginResponse[]>) => AuthActions.loginSuccess({ response })),
            catchError((error) =>
              of(AuthActions.loginFailure({ error: error.message || 'Login failed' })),
            ),
          );
      }),
    ),
  );

  // Save to sessionStorage on login success
  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      tap(({ response }) => {
        if (response && response.Rc !== 0) {
          this.notification.error(response.RsMsg || 'Login failed due to server error');
          return;
        }
      }),
      switchMap(({ response }) => {
        if (response?.Rc === 0 && response?.Data?.length > 0) {
          const responseData = response.Data[0];
          // Store refresh token in secure cookie
          const refreshTokenExpirationDays = this.getDaysRemainingFromString(
            responseData.RefreshTokenExpiration,
          );
          const accessTokenExpirationDays = this.getDaysRemainingFromString(
            responseData.ExpiresTime,
          );

          this.cookieService.setCookie(
            TokenInfoKey,
            responseData.AccessToken,
            accessTokenExpirationDays,
          );
          this.cookieService.setRefreshToken(responseData.RefreshToken, refreshTokenExpirationDays);
          this.cookieService.setCookie(
            RefreshTokenExpirationKey,
            responseData.RefreshTokenExpiration?.toString() ?? '',
            refreshTokenExpirationDays,
          );

          // Store user info in sessionStorage
          const user: AuthUser = {
            UserId: responseData.UserId,
            LoginName: responseData.LoginName,
            DisplayName: responseData.DisplayName,
            Email: responseData.Email,
            Active: responseData.Active,
            Gender: responseData.Gender,
            Birthday: responseData.Birthday,
            Address: responseData.Address,
            IsLockLogin: responseData.IsLockLogin,
            branchCode: responseData.branchCode,
            branchId: responseData.branchId,
            OrganizationId_all: responseData.OrganizationId_all
          };
          this.cookieService.setCookie(
            UserInfoKey,
            JSON.stringify(user),
            refreshTokenExpirationDays,
          );
          this.cookieService.setCookie(
            AccessTokenExpirationKey,
            responseData.ExpiresTime.toString(),
            accessTokenExpirationDays,
          );
          
          // Store menus in sessionStorage
          if (responseData.Menus) {
            sessionStorage.setItem(MenuInfoKey, JSON.stringify(responseData.Menus));
          }

          // Store functions in sessionStorage
          if (responseData.Functions) {
            sessionStorage.setItem(FunctionInfoKey, JSON.stringify(responseData.Functions));
          }

          // Load language list sau khi login thành công
          this.i18n.onLoginSuccess();

          // Dispatch both permissions and menus actions
          return [
            AuthActions.setAuthUserFromStorage({ user }),
            AuthActions.setAccessTokenToStore({ access_token: responseData.AccessToken }),
            AuthActions.loadPermissions({ permissions: responseData.Functions ?? [] }),
            AuthActions.setMenus({ menus: responseData.Menus || [] }),
          ];
        }
        return [AuthActions.loginFailure({ error: response?.RsMsg || 'Login failed' })];
      }),
    ),
  );

  // Load permissions
  loadPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadPermissions),
      switchMap(({ permissions }) => {
        return of(permissions).pipe(
          map((permissions: Function[]) => AuthActions.loadPermissionsSuccess({ permissions })),
          catchError((error) =>
            of(
              AuthActions.loadPermissionsFailure({
                error: error.message || 'Failed to load permissions',
              }),
            ),
          ),
        );
      }),
    ),
  );

  // Logout & navigate
  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      tap(() => {
        this.cookieService.removeCookie(TokenInfoKey);
        this.cookieService.removeCookie(RefreshTokenExpirationKey);
        this.cookieService.removeCookie(AccessTokenExpirationKey);
        this.cookieService.removeCookie(UserInfoKey);

        sessionStorage.removeItem(MenuInfoKey);
        sessionStorage.removeItem(FunctionInfoKey);
        // Remove refresh token cookie
        this.cookieService.removeRefreshToken();
        // Don't navigate if on kios route
        if (!window.location.pathname.startsWith('/kios')) {
          this.router.navigate(['/login']);
        }
      }),
      map(() => AuthActions.logoutSuccess()),
    ),
  );

  // Persist menus to sessionStorage when they change
  setMenus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.setMenus),
        tap(({ menus }) => {
          // Persist menus to sessionStorage
          if (menus && menus.length > 0) {
            sessionStorage.setItem(MenuInfoKey, JSON.stringify(menus));
          }
        }),
      ),
    { dispatch: false },
  );

  // Refresh User Info Effect
  refreshUserInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshUserInfo),
      switchMap(({ userId, loginName }) =>
        this.authService.refreshUserInfo(userId, loginName).pipe(
          map((response) => {
            if (response.Rc === 0 && response.Data) {
              // Extract user, menus, and functions from response
              const menus = response.Data.Menus || [];
              const functions = response.Data.Functions || [];
              const cookieUser = this.cookieService.getCookie(UserInfoKey);
              const currentUser: AuthUser = cookieUser ? JSON.parse(cookieUser) : null;

              // Store user info in sessionStorage
              const user: AuthUser = {
                UserId: response.Data.ID,
                LoginName: response.Data.C_LOGIN_NAME,
                DisplayName: response.Data.C_DISPLAY_NAME,
                Email: response.Data.C_EMAIL,
                Active: response.Data.C_ACTIVE,
                Gender: response.Data.C_GENDER,
                Birthday: response.Data.C_BIRTHDAY,
                Address: response.Data.C_ADDRESS,
                IsLockLogin: response.Data.C_IS_LOCK_LOGIN,
                branchCode: currentUser.branchCode || '',
                branchId: currentUser.branchId || 0,
              };
              this.cookieService.setCookie(UserInfoKey, JSON.stringify(user));

              // Store menus in sessionStorage
              if (menus) {
                sessionStorage.setItem(MenuInfoKey, menus);
              }

              // Store functions in sessionStorage
              if (functions) {
                sessionStorage.setItem(FunctionInfoKey, functions);
              }
              const menusParsed: Menu[] = menus ? JSON.parse(menus) : [];
              const functionsParsed: Function[] = functions ? JSON.parse(functions) : [];

              return AuthActions.refreshUserInfoSuccess({
                user,
                menus: menusParsed,
                functions: functionsParsed,
              });
            } else {
              const errorMsg = response.RsMsg;
              this.notification.error(errorMsg);
              throw new Error(errorMsg);
            }
          }),
          catchError((error) => {
            console.error('Refresh user info error:', error);
            throw new Error(error.message);
          }),
        ),
      ),
    ),
  );

  private getDaysRemainingFromString(date: Date | undefined): number {
    if (!date) {
      return 0;
    }
    return Math.ceil((new Date(date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
  }
}
